/**
 * Typed client for the booking assistant's chat endpoint.
 *
 * Mirrors the server contract in
 * src/api/Wildstream.BookingAssistant.Api/Models/ChatModels.cs — keep the
 * shapes in sync if that file changes.
 *
 * Conversation state lives server-side keyed by `conversationId`. Omit the
 * id on the first turn; the server returns one on the initial response and
 * it must be echoed back on every subsequent turn.
 */

export type PageContextPayload = {
  pageType: string;
  makeId?: string | null;
  modelId?: string | null;
};

export type ChatRequest = {
  message: string;
  conversationId?: string | null;
  pageContext?: PageContextPayload;
};

export type ConversationContextUpdate = {
  conversationId: string;
  motorizationId?: string | null;
  preferredDealerId?: string | null;
  acceptanceType?: string | null;
};

/**
 * Pushes UI-pinned state to the backend (motorization, dealer, acceptance
 * type). Call this when a card CTA selects a vehicle/dealer so the next chat
 * turn sees the choice in IConversationContext.
 */
export async function updateConversationContext(
  update: ConversationContextUpdate,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/chat/context`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(update),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `Chat context API ${response.status} ${response.statusText}${detail ? `: ${detail}` : ""}`,
    );
  }
}

// ── Content blocks (structured UI cards alongside text) ────────────

export type TypologyResult = {
  typology: string;
  typologyLabel: string;
  description: string;
  recommendations: string[];
};

// Booking content blocks — mirror
// src/api/Wildstream.BookingAssistant.Api/Models/Bookings/*.cs (camelCase).

export type DealerSummary = {
  dealerId: string;
  dealerNumber: string;
  name: string;
  brandCodes: string[];
  address: string;
  city?: string | null;
  phone?: string | null;
  distanceKm?: number | null;
  openingHoursLabel?: string | null;
};

export type SlotOption = {
  slotId: string;
  startUtc: string;
  endUtc: string;
  staffId: string;
  staffName: string;
  dealerId: string;
  acceptanceType: string;
  serviceId: string;
  label: string;
  isSynthFallback: boolean;
};

export type SlotPickerPrefill = {
  name?: string | null;
  email?: string | null;
};

export type SlotPickerPayload = {
  slots: SlotOption[];
  dealer: DealerSummary;
  acceptanceType: string;
  vehicle?: string | null;
  prefill?: SlotPickerPrefill | null;
};

export type BookingConfirmationPayload = {
  status: "confirmed" | "pending-confirmation";
  appointmentId: string;
  appointmentNumber?: string | null;
  startUtc: string;
  endUtc: string;
  dealer: DealerSummary;
  staffName?: string | null;
  acceptanceType: string;
  vehicle?: string | null;
};

// Post-booking satisfaction rating card — mirrors
// src/api/Wildstream.BookingAssistant.Api/Models/Csat/CsatRatingPayload.cs.
export type CsatRatingPayload = {
  language: string;
};

// Vehicle catalogue (new on this branch) — mirrors
// src/api/.../Models/Vehicles/*.cs and the VehicleCatalogAgent tool payloads.

export type VehicleSearchModel = {
  id: string;
  makeId: string;
  makeName: string;
  name: string;
  tagLine?: string | null;
  imageFilename?: string | null;
  marketingImages: string[];
  videoUrl?: string | null;
  startingPriceEur: number;
  monthlyFromEur?: number | null;
  horsePower?: number | null;
  rangeKm?: number | null;
  fuelType?: string | null;
  bodyType?: string | null;
  highlights?: string[] | null;
  motorizationCount: number;
};

export type VehicleSearchResult = {
  status: "ok" | "empty" | "error";
  count: number;
  filters: {
    brandId?: string | null;
    fuelType?: string | null;
    maxPriceEur?: number | null;
  };
  results: VehicleSearchModel[];
  broadening: string[];
  instruction?: string | null;
};

export type VehicleMarketingContent = {
  title?: string | null;
  content?: string | null;
  usps: string[];
  specs: Record<string, string>;
};

export type VehicleMotorization = {
  id: string;
  modelId: string;
  name: string;
  fuelType: string;
  horsePower?: number | null;
  consumption?: string | null;
  emissionsMinGramKm?: number | null;
  emissionsMaxGramKm?: number | null;
  startingPriceEur: number;
  gearBox?: string | null;
  doors?: number | null;
  seats?: number | null;
};

export type VehicleDetailModel = {
  id: string;
  makeId: string;
  makeName: string;
  name: string;
  tagLine?: string | null;
  imageFilename?: string | null;
  marketingImages: string[];
  videoUrl?: string | null;
  content?: VehicleMarketingContent | null;
};

export type VehicleDetailResult = {
  status: "ok" | "not_found" | "error";
  model?: VehicleDetailModel | null;
  motorizations?: VehicleMotorization[] | null;
  instruction?: string | null;
};

// Financing eligibility estimator — mirrors Models/Financing/FinancingEligibilityPayload.cs.

export type FinancingPaymentOption = {
  termMonths: number;
  monthlyPaymentEur: number;
  isPopular: boolean;
};

export type FinancingEligibilityResult = {
  status: "awaiting-answers" | "ok" | "insufficient-data";
  maxVehiclePriceEur?: number | null;
  assumedApr?: number | null;
  options?: FinancingPaymentOption[] | null;
  disclaimerNote?: string | null;
};

// Lease calculator — mirrors Models/Vehicles/LeaseModels.cs.

export type LeaseCalculatorResult = {
  status: "ok" | "invalid-term" | "invalid-down-payment";
  monthlyPaymentEur: number;
  termMonths: number;
  downPaymentEur: number;
  priceEur: number;
  fuelSavingsPerYearEur?: number;
  apprEstimatedInterestRate?: number;
  note?: string;
};

// Dealer-lookup tool output. Mirrors Models/DealerLocation.cs.

export type DealerLocation = {
  id: string;
  dealerNumber: string;
  name: string;
  brandCodes: string[];
  address: string;
  postalCode?: string | null;
  city?: string | null;
  cityAliases: string[];
  phone?: string | null;
  email?: string | null;
  serviceNotes?: string | null;
  distanceKm?: number | null;
  openingHoursLabel?: string | null;
};

export type DealerLocationToolResult = {
  status: "ok" | "not_found" | "brand_not_found" | "error";
  brand?: string | null;
  query?: string | null;
  count?: number;
  locations?: DealerLocation[];
  availableBrands?: string[];
  note?: string | null;
  error?: string | null;
  instruction?: string | null;
};

export type ContentBlock = {
  type: string;
  data: unknown;
};

export type ChatResponse = {
  conversationId: string;
  message: string;
  timestamp: string;
  contentBlocks?: ContentBlock[] | null;
  conversationLanguage?: string | null;
};

// Resolution order:
//   1. window.APP_CONFIG.apiUrl from /config.js -- set on the server when the
//      SPA is hosted separately from the API. Editable without a rebuild.
//   2. VITE_API_URL -- `npm run dev` sets it through .env.development, because
//      there the Vite dev server (5173) and the API (5080) are separate origins.
//   3. Empty string = same-origin relative requests: the API serves this bundle
//      out of its own wwwroot, so every /api/* call resolves against Kestrel.
export const API_BASE_URL: string = (
  window.APP_CONFIG?.apiUrl ||
  import.meta.env.VITE_API_URL ||
  ""
).replace(/\/+$/, "");

export async function sendChatMessage(
  request: ChatRequest,
  signal?: AbortSignal,
): Promise<ChatResponse> {
  const response = await fetch(`${API_BASE_URL}/api/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `Chat API ${response.status} ${response.statusText}${detail ? `: ${detail}` : ""}`,
    );
  }

  return (await response.json()) as ChatResponse;
}

// ── SSE streaming ───────────────────────────────────────────────────

export type SseEvent = {
  event: string;
  data: unknown;
};

/**
 * Streams the orchestrator response via SSE (POST-based, not EventSource).
 * Yields parsed SSE events one at a time.
 */
export async function* streamChatMessage(
  request: ChatRequest,
  signal?: AbortSignal,
): AsyncGenerator<SseEvent> {
  const response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `Chat stream API ${response.status} ${response.statusText}${detail ? `: ${detail}` : ""}`,
    );
  }

  if (!response.body) {
    throw new Error("Chat stream response has no body");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by double newlines
      const parts = buffer.split("\n\n");
      buffer = parts.pop()!; // last element is incomplete or empty

      for (const part of parts) {
        if (!part.trim()) continue;

        let eventType = "message";
        let dataLines = "";

        for (const line of part.split("\n")) {
          if (line.startsWith("event: ")) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            dataLines += (dataLines ? "\n" : "") + line.slice(6);
          } else if (line.startsWith("data:")) {
            dataLines += (dataLines ? "\n" : "") + line.slice(5);
          }
        }

        if (dataLines) {
          let parsed: unknown;
          try {
            parsed = JSON.parse(dataLines);
          } catch {
            throw new Error(
              `Failed to parse SSE event data for "${eventType}": ${dataLines}`,
            );
          }
          yield { event: eventType, data: parsed };
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
